import { keyframes, styled } from '@mui/material';

import { useEffect, useMemo, useState } from 'react';

import { SimpleStatus } from '@app-velocity/common';
import { useI18n } from '@app-velocity/i18n';
import { InfoOutlineIcon } from '@app-velocity/icons';
import { StatusBackground, Tooltip, Typography } from '@app-velocity/ui';

import { useLoopContext } from '../../../../hooks/useLoopContext';
import { useSingleAiResponseContext } from '../../hooks/useSingleAiResponseContext';
import { useAiChat } from '../../../../hooks/useAiChat';

const fadeIn = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const StyledIndicator = styled('div')<{
  hovered: boolean;
}>(({ theme, hovered }) => ({
  position: 'absolute',
  width: hovered ? '80%' : 6,
  height: '100%',
  zIndex: 1,
  transition: 'all .5s',
  borderRadius: hovered ? theme.spacing(2) : 0,
  overflow: 'hidden',
  color: hovered ? 'var(--text-text-alt-color)' : 'transparent',
  svg: {
    fill: hovered ? 'var(--text-text-alt-color)' : 'transparent',
  },
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  padding: hovered ? theme.spacing(4) : `${theme.spacing(4)} 0`,
  cursor: 'pointer',
}));

const Title = styled(Typography)({
  fontSize: 13,

  fontWeight: 500,
});

const Description = styled(Typography)({
  fontSize: 12,
  lineHeight: 1.2,
  fontWeight: 300,
});

const Data = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  animation: `${fadeIn} 0.5s cubic-bezier(0.5, 0, 0.5, 1)`,
  width: 220,
});

const Header = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const typeToStatus = {
  high: 'success',
  medium: 'warning',
  low: 'error',
};

export type ConfidenceScoreProps = {
  opened: boolean;
};

export const ConfidenceScore = ({ opened }: ConfidenceScoreProps) => {
  const loop = useLoopContext();
  const { stepNumber, response } = useSingleAiResponseContext();
  const { messages } = useAiChat();
  const [hovered, setHovered] = useState(false);
  const { t } = useI18n();

  const previousAttempts = useMemo(() => {
    const realIndex =
      stepNumber - 1 - (loop ? loop.unaffectedMessagesCount : 0);

    if (realIndex <= 0) return 0;
    const thisMessage = messages[realIndex];
    let previousAttemptsCount = 0;

    for (let i = realIndex - 1; i >= 0; i--) {
      if (messages[i].pageId !== thisMessage.pageId) break;

      if (messages[i].systemResponse?.type === 'ACTION_FOUND') {
        break;
      }

      if (messages[i].systemResponse?.type === 'NO_SUITABLE_ACTION_FOUND') {
        previousAttemptsCount++;
      }
    }

    return previousAttemptsCount;
  }, []);

  const type = useMemo(() => {
    if (!response) return null;
    const { confidence } = response;

    // @TODO - temporary disabled to match report
    // if (previousAttempts === 1) return 'medium';

    // if (previousAttempts > 1) {
    //   if (confidence && ['high', 'medium'].includes(confidence))
    //     return 'medium';

    //   return 'low';
    // }

    return confidence || 'high';
  }, [previousAttempts, response]);

  const handleHover = () => {
    setHovered(true);
  };

  const handleBlur = () => {
    setHovered(false);
  };

  useEffect(() => {
    setHovered(opened);
  }, [opened]);

  if (!response || !type) return null;

  const { confidenceExplanation } = response;

  return (
    <StyledIndicator
      onMouseEnter={handleHover}
      onMouseLeave={handleBlur}
      hovered={hovered}
    >
      <StatusBackground status={typeToStatus[type] as SimpleStatus} noPattern />
      {/* {hovered ? ( */}
      <Data>
        <Header>
          <Title>{t(`prototypes:confidence_score.${type}`)}</Title>
          <Tooltip
            title={`${t('prototypes:confidence_score.tooltip')} ${t(
              `prototypes:confidence_score.tooltip_${type}`
            )}`}
          >
            <InfoOutlineIcon />
          </Tooltip>
        </Header>
        {previousAttempts > 0 ? (
          <Description>
            {t('prototypes:confidence_score.repeated_action', {
              index: previousAttempts + 1,
            })}
          </Description>
        ) : null}
        {confidenceExplanation ? (
          <Description>{confidenceExplanation}</Description>
        ) : null}
      </Data>
      {/* ) : null} */}
    </StyledIndicator>
  );
};
